import React, { useState } from 'react';
import { Card, Collapse, Input, InputNumber, Select, Button, Alert, Tag, Space, Divider, Statistic, Row, Col } from 'antd';
import { EditOutlined, WarningOutlined, PauseCircleOutlined, PictureOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import CiblageVilles from './CiblageVilles';
import GenerateurImageAnnonce from './GenerateurImageAnnonce';
import MetaAdsPlacementPreview from './MetaAdsPlacementPreview';

/**
 * Écran de revue du brouillon généré par l'IA.
 *
 * Tous les champs sont éditables avant publication : nom de campagne, budgets,
 * ciblage, textes publicitaires et bouton d'action. Le composant est contrôlé :
 * chaque modification remonte au parent via onChange.
 */

const { TextArea } = Input;

// Limites de caractères Meta, affichées comme compteur sous chaque champ
const LIMITES = { primaryText: 300, headline: 40, description: 60 };

/**
 * @param {object[]} [audiences]         - Audiences du compte, fournies par la page.
 *   Liste partagée avec l'onglet « Audiences » : une audience devenue diffusable
 *   chez Meta cesse d'être grisée sans avoir à recommencer un brief.
 * @param {boolean}  [audiencesLoading]  - Rechargement en cours
 * @param {function} [onRafraichirAudiences] - Recharge la liste partagée
 */
const MetaAdsDraftReview = ({
  draft,
  onChange,
  onPublish,
  publishing,
  audiences = [],
  audiencesLoading = false,
  onRafraichirAudiences
}) => {
  // Annonce dont le visuel est en cours de génération : { indexAdSet, indexAd }
  const [generationVisuel, setGenerationVisuel] = useState(null);

  if (!draft) return null;

  /** Met à jour un champ de premier niveau du brouillon (nom de campagne, etc.) */
  const modifierCampagne = (champ, valeur) => {
    onChange({ ...draft, campaign: { ...draft.campaign, [champ]: valeur } });
  };

  /** Met à jour une configuration ponctuelle de la campagne, comme son numéro WhatsApp. */
  const modifierOverride = (champ, valeur) => {
    onChange({ ...draft, overrides: { ...(draft.overrides || {}), [champ]: valeur } });
  };

  /** Met à jour un champ d'un ensemble de publicités */
  const modifierAdSet = (index, champ, valeur) => {
    const adSets = draft.adSets.map((adSet, i) => (i === index ? { ...adSet, [champ]: valeur } : adSet));
    onChange({ ...draft, adSets });
  };

  /** Met à jour un champ de ciblage d'un ensemble de publicités */
  const modifierCiblage = (index, champ, valeur) => {
    const adSets = draft.adSets.map((adSet, i) =>
      i === index ? { ...adSet, targeting: { ...adSet.targeting, [champ]: valeur } } : adSet
    );
    onChange({ ...draft, adSets });
  };

  /** Met à jour un champ d'une annonce donnée */
  const modifierAnnonce = (indexAdSet, indexAd, champ, valeur) => {
    const adSets = draft.adSets.map((adSet, i) => {
      if (i !== indexAdSet) return adSet;
      const ads = adSet.ads.map((ad, j) => (j === indexAd ? { ...ad, [champ]: valeur } : ad));
      return { ...adSet, ads };
    });
    onChange({ ...draft, adSets });
  };

  /** Met à jour un champ d'une carte précise, sans affecter les autres cartes du carrousel. */
  const modifierCarteCarrousel = (indexAdSet, indexAd, indexCarte, champ, valeur) => {
    const cartes = draft.adSets[indexAdSet].ads[indexAd].carouselCards.map((carte, index) => (
      index === indexCarte ? { ...carte, [champ]: valeur } : carte
    ));
    modifierAnnonce(indexAdSet, indexAd, 'carouselCards', cartes);
  };

  /** Active le format choisi et initialise deux cartes afin que le carrousel soit valide pour Meta. */
  const modifierFormatAnnonce = (indexAdSet, indexAd, format) => {
    if (format === 'single_image') {
      modifierAnnonce(indexAdSet, indexAd, 'format', format);
      return;
    }

    const ad = draft.adSets[indexAdSet].ads[indexAd];
    const carteParDefaut = {
      imageUrl: ad.imageUrl || '',
      headline: ad.headline || '',
      description: ad.description || '',
      linkUrl: ''
    };
    const carouselCards = Array.isArray(ad.carouselCards) && ad.carouselCards.length >= 2
      ? ad.carouselCards
      : [carteParDefaut, { ...carteParDefaut }];
    const slideshowImages = Array.isArray(ad.slideshowImages) && ad.slideshowImages.length >= 2
      ? ad.slideshowImages
      : [ad.imageUrl || '', ad.imageUrl || ''];
    const adSets = draft.adSets.map((adSet, adSetIndex) => {
      if (adSetIndex !== indexAdSet) return adSet;
      return {
        ...adSet,
        ads: adSet.ads.map((annonce, annonceIndex) => (
          annonceIndex === indexAd ? { ...annonce, format, carouselCards, slideshowImages } : annonce
        ))
      };
    });
    onChange({ ...draft, adSets });
  };

  /** Ajoute une carte, dans la limite des dix cartes autorisées par Meta. */
  const ajouterCarteCarrousel = (indexAdSet, indexAd) => {
    const ad = draft.adSets[indexAdSet].ads[indexAd];
    const cartes = ad.carouselCards || [];
    if (cartes.length >= 10) return;
    modifierAnnonce(indexAdSet, indexAd, 'carouselCards', [...cartes, {
      imageUrl: ad.imageUrl || '', headline: ad.headline || '', description: ad.description || '', linkUrl: ''
    }]);
  };

  /** Supprime une carte uniquement si le minimum de deux cartes reste présent. */
  const supprimerCarteCarrousel = (indexAdSet, indexAd, indexCarte) => {
    const cartes = draft.adSets[indexAdSet].ads[indexAd].carouselCards || [];
    if (cartes.length <= 2) return;
    modifierAnnonce(indexAdSet, indexAd, 'carouselCards', cartes.filter((_, index) => index !== indexCarte));
  };

  /** Déplace une carte d'un rang afin de contrôler l'ordre d'affichage dans Meta. */
  const deplacerCarteCarrousel = (indexAdSet, indexAd, indexCarte, direction) => {
    const cartes = [...(draft.adSets[indexAdSet].ads[indexAd].carouselCards || [])];
    const destination = indexCarte + direction;
    if (destination < 0 || destination >= cartes.length) return;
    [cartes[indexCarte], cartes[destination]] = [cartes[destination], cartes[indexCarte]];
    modifierAnnonce(indexAdSet, indexAd, 'carouselCards', cartes);
  };

  /** Met à jour la liste ordonnée des images qui composeront le slideshow vidéo. */
  const modifierImagesSlideshow = (indexAdSet, indexAd, images) => {
    modifierAnnonce(indexAdSet, indexAd, 'slideshowImages', images);
  };

  /** Ajoute une image au slideshow, dans la limite de dix images afin de garder une vidéo courte. */
  const ajouterImageSlideshow = (indexAdSet, indexAd) => {
    const ad = draft.adSets[indexAdSet].ads[indexAd];
    const images = ad.slideshowImages || [];
    if (images.length >= 10) return;
    modifierImagesSlideshow(indexAdSet, indexAd, [...images, ad.imageUrl || '']);
  };

  /** Retire une image sans descendre sous le minimum de deux images nécessaire à un slideshow. */
  const supprimerImageSlideshow = (indexAdSet, indexAd, indexImage) => {
    const images = draft.adSets[indexAdSet].ads[indexAd].slideshowImages || [];
    if (images.length <= 2) return;
    modifierImagesSlideshow(indexAdSet, indexAd, images.filter((_, index) => index !== indexImage));
  };

  /** Déplace une image afin de définir la séquence de la vidéo générée. */
  const deplacerImageSlideshow = (indexAdSet, indexAd, indexImage, direction) => {
    const images = [...(draft.adSets[indexAdSet].ads[indexAd].slideshowImages || [])];
    const destination = indexImage + direction;
    if (destination < 0 || destination >= images.length) return;
    [images[indexImage], images[destination]] = [images[destination], images[indexImage]];
    modifierImagesSlideshow(indexAdSet, indexAd, images);
  };

  // Somme des budgets : sert à alerter si l'utilisateur casse la répartition
  const sommeBudgets = draft.adSets.reduce((total, a) => total + (a.dailyBudget || 0), 0);
  const budgetIncoherent = sommeBudgets !== draft.dailyBudget;

  return (
    <div className="space-y-6">
      {/* Résumé de la campagne */}
      <Card
        title={
          <div className="flex items-center space-x-2">
            <EditOutlined className="text-blue-600" />
            <span>Brouillon de campagne — à valider</span>
          </div>
        }
        className="shadow-sm"
      >
        {draft.warnings?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            className="mb-4"
            message="Points à vérifier avant publication"
            description={
              <ul className="list-disc pl-4 mb-0">
                {draft.warnings.map((avertissement, i) => <li key={i}>{avertissement}</li>)}
              </ul>
            }
          />
        )}

        <Row gutter={[16, 12]} className="mb-4">
          <Col xs={24} sm={8}>
            <Statistic title="Budget journalier" value={draft.dailyBudget} suffix={draft.currency} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title={`Budget total (${draft.durationDays} j)`} value={draft.totalBudget} suffix={draft.currency} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Ensembles de publicités" value={draft.adSets.length} />
          </Col>
        </Row>

        {budgetIncoherent && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={`La somme des budgets (${sommeBudgets} ${draft.currency}) ne correspond pas au budget journalier (${draft.dailyBudget} ${draft.currency}).`}
          />
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Nom de la campagne</label>
          <Input
            value={draft.campaign.name}
            onChange={e => modifierCampagne('name', e.target.value)}
          />
        </div>

        <Tag color="blue" className="private-tag-accent" >{draft.objectiveLabel}</Tag>

        {draft.objective === 'whatsapp' && (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">Numéro WhatsApp de cette campagne</label>
            <Input
              value={draft.overrides?.whatsappNumber || ''}
              onChange={event => modifierOverride('whatsappNumber', event.target.value)}
              placeholder="+2250700000000"
            />
            <p className="mt-1 mb-0 text-xs text-gray-500">
              Ce numéro doit être rattaché à la Page Facebook et au compte publicitaire dans Meta Business Manager.
            </p>
          </div>
        )}

        {draft.campaign.rationale && (
          <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-700">
            <strong>Stratégie proposée :</strong> {draft.campaign.rationale}
          </div>
        )}

        {draft.campaign.revenuePlan && (
          <div className="mt-3 p-3 bg-green-50 rounded text-sm text-green-800">
            <strong>Plan pour augmenter le revenu :</strong> {draft.campaign.revenuePlan}
          </div>
        )}

        {draft.notes && (
          <div className="mt-3 p-3 bg-blue-50 rounded text-sm text-blue-800">
            <strong>Conseils de l'IA :</strong> {draft.notes}
          </div>
        )}
      </Card>

      {/* Ensembles de publicités */}
      <Collapse
        defaultActiveKey={draft.adSets.map((_, i) => String(i))}
        items={draft.adSets.map((adSet, index) => ({
          key: String(index),
          label: (
            <div className="flex justify-between items-center pr-4">
              <span className="font-medium">{adSet.name}</span>
              <Tag color="green">{adSet.dailyBudget} {draft.currency} / jour</Tag>
            </div>
          ),
          children: (
            <div className="space-y-4">
              <Row gutter={[16, 12]}>
                <Col xs={24} sm={12}>
                  <label className="block text-sm font-medium mb-1">Nom de l'ensemble</label>
                  <Input
                    value={adSet.name}
                    onChange={e => modifierAdSet(index, 'name', e.target.value)}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <label className="block text-sm font-medium mb-1">Budget journalier</label>
                  <InputNumber
                    min={0}
                    step={500}
                    style={{ width: '100%' }}
                    addonAfter={draft.currency}
                    value={adSet.dailyBudget}
                    onChange={valeur => modifierAdSet(index, 'dailyBudget', valeur || 0)}
                  />
                </Col>
              </Row>

              <Divider orientation="left" plain>Ciblage</Divider>

              <Row gutter={[16, 12]}>
                <Col xs={24} sm={8}>
                  <label className="block text-sm font-medium mb-1">Pays</label>
                  <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    value={adSet.targeting.countries}
                    onChange={valeur => modifierCiblage(index, 'countries', valeur)}
                  />
                </Col>
                <Col xs={12} sm={4}>
                  <label className="block text-sm font-medium mb-1">Âge min</label>
                  <InputNumber
                    min={18}
                    max={65}
                    style={{ width: '100%' }}
                    value={adSet.targeting.ageMin}
                    onChange={valeur => modifierCiblage(index, 'ageMin', valeur)}
                  />
                </Col>
                <Col xs={12} sm={4}>
                  <label className="block text-sm font-medium mb-1">Âge max</label>
                  <InputNumber
                    min={18}
                    max={65}
                    style={{ width: '100%' }}
                    value={adSet.targeting.ageMax}
                    onChange={valeur => modifierCiblage(index, 'ageMax', valeur)}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <label className="block text-sm font-medium mb-1">Sexe</label>
                  <Select
                    style={{ width: '100%' }}
                    value={adSet.targeting.gender}
                    onChange={valeur => modifierCiblage(index, 'gender', valeur)}
                    options={[
                      { value: 'all', label: 'Tous' },
                      { value: 'female', label: 'Femmes' },
                      { value: 'male', label: 'Hommes' }
                    ]}
                  />
                </Col>
              </Row>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Villes ciblées avec rayon (optionnel)
                </label>
                <CiblageVilles
                  villes={adSet.targeting.cities || []}
                  onChange={valeur => modifierCiblage(index, 'cities', valeur)}
                />
              </div>

              <div>
                {/* Le bouton de rechargement évite de recommencer un brief quand Meta
                    finit de construire une audience pendant l'édition du brouillon */}
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">
                    Audiences ciblées — stratégie IA : {adSet.targeting.audienceStrategy === 'lookalike' ? 'similaire' : adSet.targeting.audienceStrategy === 'retargeting' ? 'retargeting' : 'audience large'}
                  </label>
                  {onRafraichirAudiences && (
                    <Button
                      size="small"
                      type="text"
                      icon={<ReloadOutlined />}
                      loading={audiencesLoading}
                      onClick={onRafraichirAudiences}
                    >
                      Rafraîchir
                    </Button>
                  )}
                </div>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder={audiences.length > 0
                    ? 'Audiences personnalisées ou similaires'
                    : 'Aucune audience sur ce compte'}
                  value={adSet.targeting.customAudiences || []}
                  onChange={valeur => modifierCiblage(index, 'customAudiences', valeur)}
                  options={audiences.map(a => ({
                    value: a.id,
                    label: `${a.name}${a.subtype === 'LOOKALIKE' ? ' · similaire' : ''}`,
                    // Une audience encore en construction chez Meta n'est pas diffusable
                    disabled: !a.pretePourDiffusion
                  }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Centres d'intérêt</label>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="Libellés Meta (en anglais)"
                  value={adSet.targeting.interests}
                  onChange={valeur => modifierCiblage(index, 'interests', valeur)}
                />
              </div>

              <Divider orientation="left" plain>Annonces</Divider>

              {adSet.ads.map((ad, indexAd) => (
                <Card
                  key={indexAd}
                  size="small"
                  className="bg-gray-50"
                  title={
                    <Space>
                      <span>{`Annonce ${indexAd + 1}`}</span>
                      {/* Angle marketing choisi par l'IA pour cette variante */}
                      {ad.angle && <Tag color="purple">{ad.angle}</Tag>}
                    </Space>
                  }
                >
                  <Row gutter={[16, 12]}>
                    {/* Aperçu du visuel choisi par l'IA parmi les images réelles du produit */}
                    <Col xs={24} sm={6}>
                      <MetaAdsPlacementPreview ad={ad} />

                      {/* Génération d'un visuel dédié à l'angle de cette annonce */}
                      <Button
                        block
                        size="small"
                        className="mt-2"
                        icon={<PictureOutlined />}
                        onClick={() => setGenerationVisuel({ indexAdSet: index, indexAd })}
                      >
                        {ad.imageUrl ? 'Changer le visuel' : 'Choisir un visuel'}
                      </Button>
                    </Col>

                    <Col xs={24} sm={18}>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">Format</label>
                          <Select
                            style={{ width: '100%' }}
                            value={ad.format || 'single_image'}
                            onChange={valeur => modifierFormatAnnonce(index, indexAd, valeur)}
                            options={[
                              { value: 'single_image', label: 'Image unique' },
                              { value: 'carousel', label: 'Carrousel (plusieurs images)', disabled: draft.creativeType === 'catalog' },
                              { value: 'slideshow', label: 'Slideshow vidéo (images automatiques)', disabled: draft.creativeType === 'catalog' }
                            ]}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Texte principal ({(ad.primaryText || '').length}/{LIMITES.primaryText})
                          </label>
                          <TextArea
                            rows={3}
                            maxLength={LIMITES.primaryText}
                            value={ad.primaryText}
                            onChange={e => modifierAnnonce(index, indexAd, 'primaryText', e.target.value)}
                          />
                        </div>

                        <Row gutter={[16, 12]}>
                          <Col xs={24} sm={12}>
                            <label className="block text-sm font-medium mb-1">
                              Titre ({(ad.headline || '').length}/{LIMITES.headline})
                            </label>
                            <Input
                              maxLength={LIMITES.headline}
                              value={ad.headline}
                              onChange={e => modifierAnnonce(index, indexAd, 'headline', e.target.value)}
                            />
                          </Col>
                          <Col xs={24} sm={12}>
                            <label className="block text-sm font-medium mb-1">
                              Description ({(ad.description || '').length}/{LIMITES.description})
                            </label>
                            <Input
                              maxLength={LIMITES.description}
                              value={ad.description}
                              onChange={e => modifierAnnonce(index, indexAd, 'description', e.target.value)}
                            />
                          </Col>
                        </Row>

                        <div>
                          <label className="block text-sm font-medium mb-1">Bouton d'action</label>
                          <Select
                            style={{ width: '100%' }}
                            value={ad.callToAction}
                            onChange={valeur => modifierAnnonce(index, indexAd, 'callToAction', valeur)}
                            options={(draft.allowedCallToActions || [ad.callToAction]).map(cta => ({
                              value: cta,
                              label: cta
                            }))}
                          />
                        </div>

                        {ad.format === 'carousel' && (
                          <div className="space-y-3">
                            <Divider orientation="left" plain>Cartes du carrousel ({(ad.carouselCards || []).length}/10)</Divider>
                            {(ad.carouselCards || []).map((carte, indexCarte) => (
                              <Card
                                key={indexCarte}
                                size="small"
                                title={`Carte ${indexCarte + 1}`}
                                extra={<Space>
                                  <Button size="small" icon={<ArrowUpOutlined />} disabled={indexCarte === 0} onClick={() => deplacerCarteCarrousel(index, indexAd, indexCarte, -1)} />
                                  <Button size="small" icon={<ArrowDownOutlined />} disabled={indexCarte === ad.carouselCards.length - 1} onClick={() => deplacerCarteCarrousel(index, indexAd, indexCarte, 1)} />
                                  <Button size="small" danger icon={<DeleteOutlined />} disabled={ad.carouselCards.length <= 2} onClick={() => supprimerCarteCarrousel(index, indexAd, indexCarte)} />
                                </Space>}
                              >
                                <Row gutter={[8, 12]}>
                                  <Col xs={24} sm={6}>
                                    {carte.imageUrl ? <img src={carte.imageUrl} alt={carte.headline} className="w-full aspect-square object-cover rounded" /> : <div className="bg-gray-200 aspect-square rounded" />}
                                    <Button block size="small" className="mt-2" icon={<PictureOutlined />} onClick={() => setGenerationVisuel({ indexAdSet: index, indexAd, indexCarte })}>Choisir l'image</Button>
                                  </Col>
                                  <Col xs={24} sm={18} className="space-y-2">
                                    <Input placeholder="Titre" maxLength={LIMITES.headline} value={carte.headline} onChange={e => modifierCarteCarrousel(index, indexAd, indexCarte, 'headline', e.target.value)} />
                                    <Input placeholder="Description" maxLength={LIMITES.description} value={carte.description} onChange={e => modifierCarteCarrousel(index, indexAd, indexCarte, 'description', e.target.value)} />
                                    <Input placeholder="Lien de destination (optionnel)" value={carte.linkUrl} onChange={e => modifierCarteCarrousel(index, indexAd, indexCarte, 'linkUrl', e.target.value)} />
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                            <Button icon={<PlusOutlined />} disabled={(ad.carouselCards || []).length >= 10} onClick={() => ajouterCarteCarrousel(index, indexAd)}>Ajouter une carte</Button>
                          </div>
                        )}

                        {ad.format === 'slideshow' && (
                          <div className="space-y-3">
                            <Alert type="info" showIcon message="Les images s'enchaînent automatiquement dans une vidéo courte." />
                            <Divider orientation="left" plain>Images du slideshow ({(ad.slideshowImages || []).length}/10)</Divider>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                              {(ad.slideshowImages || []).map((imageUrl, indexImage) => (
                                <Card
                                  key={indexImage}
                                  size="small"
                                  title={`Image ${indexImage + 1}`}
                                  cover={imageUrl ? <img src={imageUrl} alt={`Diapositive ${indexImage + 1}`} className="aspect-square object-cover" /> : <div className="bg-gray-200 aspect-square" />}
                                  actions={[
                                    <ArrowUpOutlined key="up" onClick={() => deplacerImageSlideshow(index, indexAd, indexImage, -1)} className={indexImage === 0 ? 'text-gray-300' : ''} />,
                                    <ArrowDownOutlined key="down" onClick={() => deplacerImageSlideshow(index, indexAd, indexImage, 1)} className={indexImage === ad.slideshowImages.length - 1 ? 'text-gray-300' : ''} />,
                                    <PictureOutlined key="image" onClick={() => setGenerationVisuel({ indexAdSet: index, indexAd, indexImage, type: 'slideshow' })} />,
                                    <DeleteOutlined key="delete" onClick={() => supprimerImageSlideshow(index, indexAd, indexImage)} className={ad.slideshowImages.length <= 2 ? 'text-gray-300' : 'text-red-500'} />
                                  ]}
                                />
                              ))}
                            </div>
                            <Button icon={<PlusOutlined />} disabled={(ad.slideshowImages || []).length >= 10} onClick={() => ajouterImageSlideshow(index, indexAd)}>Ajouter une image</Button>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
          )
        }))}
      />

      {/* Publication : disponible au lot 3 */}
      <Card className="shadow-sm">
        <Space>
          <Button
            type="primary"
            icon={<PauseCircleOutlined />}
            loading={publishing}
            disabled={!onPublish || budgetIncoherent}
            onClick={() => onPublish && onPublish(draft)}
          >
            Créer sur Meta (en pause)
          </Button>
          <span className="text-gray-500 text-sm">
            La campagne sera créée en statut PAUSE : aucune dépense tant que vous ne l'activez pas.
          </span>
        </Space>
      </Card>

      {/* Génération du visuel de l'annonce sélectionnée : l'image retenue devient son imageUrl */}
      {generationVisuel && (
        <GenerateurImageAnnonce
          open
          ad={draft.adSets[generationVisuel.indexAdSet].ads[generationVisuel.indexAd]}
          productId={draft.adSets[generationVisuel.indexAdSet].productId}
          countries={draft.adSets[generationVisuel.indexAdSet].targeting?.countries || ['CI']}
          onSelect={(url) => {
            if (generationVisuel.type === 'slideshow') {
              const images = [...(draft.adSets[generationVisuel.indexAdSet].ads[generationVisuel.indexAd].slideshowImages || [])];
              images[generationVisuel.indexImage] = url;
              modifierImagesSlideshow(generationVisuel.indexAdSet, generationVisuel.indexAd, images);
            } else if (generationVisuel.indexCarte !== undefined) {
              modifierCarteCarrousel(generationVisuel.indexAdSet, generationVisuel.indexAd, generationVisuel.indexCarte, 'imageUrl', url);
            } else {
              modifierAnnonce(generationVisuel.indexAdSet, generationVisuel.indexAd, 'imageUrl', url);
            }
          }}
          onClose={() => setGenerationVisuel(null)}
        />
      )}
    </div>
  );
};

export default MetaAdsDraftReview;
